import { SRP_STAR_BUCKETS } from '@/src/constants/hotels'
import {
    FacetCount,
    GeoPoint,
    HotelCardModel,
    HotelSearchData,
    HotelSearchFacets,
    HotelSearchResponse,
    HotelSearchViewModel,
    RawHotelRow
} from '@/src/types/hotels/srp'
import { haversineKm } from './geoDistance'

// Old flyin ignores landmarks further than this from the hotel; the same bound drops a
// city centre that is plainly wrong.
const MAX_NEARBY_KM = 50

const toCoordinate = (value: unknown): number | null => {
    const number = Number(value)
    return Number.isFinite(number) && number !== 0 ? number : null
}

/** The city's landmarks from the search response, named in the search language. */
export const toLandmarks = (
    landMarkData: NonNullable<HotelSearchResponse['data']>['landMarkData'],
    language: string
): GeoPoint[] =>
    Object.values(landMarkData || {})
        .flat()
        .map(landmark => {
            const lat = toCoordinate(landmark?.lat)
            const lng = toCoordinate(landmark?.lng)
            const name = (language === 'ar' ? landmark?.name?.ar : '') || landmark?.name?.en || ''
            return lat != null && lng != null && name ? { name, lat, lng } : null
        })
        .filter((landmark): landmark is GeoPoint => landmark !== null)

/** The city centre from the search response. Unnamed: the card labels it. */
export const toCityCentre = (cityGeoData: NonNullable<HotelSearchResponse['data']>['cityGeoData']): GeoPoint | null => {
    const lat = toCoordinate(cityGeoData?.lat)
    const lng = toCoordinate(cityGeoData?.long)
    return lat != null && lng != null ? { name: '', lat, lng } : null
}

/** The nearest landmark within reach, else the city centre, else nothing. */
const findNearestPlace = (
    lat: number | null,
    lng: number | null,
    landmarks: GeoPoint[],
    cityCentre: GeoPoint | null
): { label: string; distanceKm: number } | null => {
    if (lat == null || lng == null) return null

    let nearest: { label: string; distanceKm: number } | null = null
    landmarks.forEach(landmark => {
        const distanceKm = haversineKm(lat, lng, landmark.lat, landmark.lng)
        if (distanceKm < MAX_NEARBY_KM && (!nearest || distanceKm < nearest.distanceKm)) {
            nearest = { label: landmark.name, distanceKm }
        }
    })
    if (!nearest && cityCentre) {
        const distanceKm = haversineKm(lat, lng, cityCentre.lat, cityCentre.lng)
        if (distanceKm < MAX_NEARBY_KM) nearest = { label: '', distanceKm }
    }
    return nearest
}

const roundToTenth = (value: number): number => Math.max(0.1, Math.round(value * 10) / 10)

const AMENITY_KEYS = [
    ['parking', 'parkingAvail'],
    ['wifi', 'wifiAvail'],
    ['swim', 'swimAvail'],
    ['gym', 'gymAvail'],
    ['roomService', 'roomSerAvail'],
    ['bar', 'barAvail']
] as const

const firstValue = (record: Record<string, string> | undefined): string => {
    if (!record) return ''
    const values = Object.values(record).filter(Boolean)
    return values[0] || ''
}

/**
 * The upstream writes the literal "Others" into `distName` when a hotel has no district
 * (me-flyin `hotelSearchResults.js`: `district.name ? … : "Others"`), so it is a placeholder
 * and not a locality. Treated as absent, leaving the card showing just the city — what the
 * apps show for a hotel with no district.
 */
const dropOthers = (value?: string | null): string =>
    !value || value.trim().toLowerCase() === 'others' ? '' : value

const normalizeRow = (
    row: RawHotelRow,
    fallbackCurrency: string,
    showSupplier: boolean,
    landmarks: GeoPoint[],
    cityCentre: GeoPoint | null
): HotelCardModel | null => {
    const staticData = row.staticData
    const name = staticData?.hotelName
    const price = row.up?.p ?? row.p ?? 0
    if (!name || price <= 0) return null

    const wasPrice = row.up?.wdp ?? row.wdp ?? price
    const hasDiscount = wasPrice > price
    const discountPercent = hasDiscount ? Math.max(1, Math.round(((wasPrice - price) / wasPrice) * 100)) : 0
    const facilities = staticData?.facilities || {}
    // `distName` is the only locality the search response carries; `district` is never sent.
    const district = dropOthers(staticData?.distName)
    const city = staticData?.cityName || staticData?.city || ''
    const accommodationType = firstValue(staticData?.hotelType) || 'Hotel'
    const distanceRaw = Number(row.nearest?.distance)
    // Coords arrive as numeric strings; 0/blank means "unknown", so drop those.
    const lat = Number(staticData?.lat)
    const lng = Number(staticData?.lng)
    const latitude = Number.isFinite(lat) && lat !== 0 ? lat : null
    const longitude = Number.isFinite(lng) && lng !== 0 ? lng : null
    const upstreamLandmark = row.nearest?.landmarkName || row.nearest?.hotelName || row.nearest?.name || ''
    const upstreamDistance = Number.isFinite(distanceRaw) && distanceRaw > 0 ? distanceRaw : null
    // The upstream never sends `nearest` for a search today, so the place is worked out here
    // from the city's landmarks and centre, the way old flyin did in the browser.
    const nearestPlace = upstreamLandmark ? null : findNearestPlace(latitude, longitude, landmarks, cityCentre)

    return {
        id: row.uid || '',
        name,
        hotelType: accommodationType,
        area: [district, city].filter(Boolean).join(', '),
        address: staticData?.addr?.trim() || '',
        imageUrl: staticData?.img || staticData?.thumbnail || '',
        rating: staticData?.taRating?.rating || 0,
        reviewCount: staticData?.taRating?.num_reviews || 0,
        starRating: staticData?.starRating || 0,
        price,
        wasPrice,
        currency: row.up?.uc || row.cur || fallbackCurrency,
        discountPercent,
        hotDeal: row.hotDeal === true || discountPercent > 0,
        amenities: {
            parking: !!facilities.parkingAvail,
            wifi: !!facilities.wifiAvail,
            swim: !!facilities.swimAvail,
            gym: !!facilities.gymAvail,
            roomService: !!facilities.roomSerAvail,
            bar: !!facilities.barAvail
        },
        // Blank unless the upstream granted CRS visibility, so the card has nothing to hide.
        supplier: showSupplier ? row.crs || '' : '',
        // An empty label with a distance means the city centre, which the card names.
        landmarkLabel: nearestPlace ? nearestPlace.label : upstreamLandmark,
        distanceKm: nearestPlace ? roundToTenth(nearestPlace.distanceKm) : upstreamDistance,
        district,
        accommodationType,
        latitude,
        longitude
    }
}

const countBy = (hotels: HotelCardModel[], pick: (hotel: HotelCardModel) => string): FacetCount[] => {
    const counts = new Map<string, number>()
    hotels.forEach(hotel => {
        const value = dropOthers(pick(hotel))
        if (!value) return
        counts.set(value, (counts.get(value) || 0) + 1)
    })
    return Array.from(counts.entries())
        .map(([value, count]) => ({ value, label: value, count }))
        .sort((a, b) => b.count - a.count)
}

const buildFacets = (hotels: HotelCardModel[]): HotelSearchFacets => {
    const prices = hotels.map(hotel => hotel.price)
    const stars = SRP_STAR_BUCKETS.map(star => ({
        star,
        count: hotels.filter(hotel => Math.round(hotel.starRating) === star).length
    })).filter(bucket => bucket.count > 0)

    const amenities: FacetCount[] = AMENITY_KEYS.map(([key]) => ({
        value: key,
        label: key,
        count: hotels.filter(hotel => hotel.amenities[key]).length
    })).filter(facet => facet.count > 0)

    return {
        priceMin: prices.length ? Math.floor(Math.min(...prices)) : 0,
        priceMax: prices.length ? Math.ceil(Math.max(...prices)) : 0,
        stars,
        hasGuestRating: hotels.some(hotel => hotel.rating > 0),
        hotDealCount: hotels.filter(hotel => hotel.hotDeal).length,
        amenities,
        propertyTypes: countBy(hotels, hotel => hotel.accommodationType),
        districts: countBy(hotels, hotel => hotel.district)
    }
}

// searchHotels rows → normalized cards + client-side facet metadata.
export const transformHotelSearch = (data: HotelSearchData): HotelSearchViewModel => {
    const hotels = (data.rows || [])
        .map(row => normalizeRow(row, data.currency, data.showSupplier, data.landmarks, data.cityCentre))
        .filter((hotel): hotel is HotelCardModel => hotel !== null)

    return {
        echoToken: data.echoToken,
        currency: data.currency,
        hotels,
        facets: buildFacets(hotels)
    }
}
